import React, { useEffect, useState } from 'react';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { Moment } from 'moment';
import moment from 'moment';
import { hapticsImpactLight } from '../../../utils/haptics';
import useWindowSize from 'react-use/lib/useWindowSize';
import Confetti from 'react-confetti';
import { useUserExamUpdate } from '../../../utils/updateUserExam';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import AnimatedCheckmark from '../../../components/Global/AnimatedCheckmark';
import ExamAccordion from '../../../components/Global/ExamAccordion';
import { fetchExams } from '../../../services/exams';
import { UserExam } from '../../../types/User';
import { useRefreshStudySessionAndContent } from '../../../utils/refreshStudySession';
import {
  defaultFlashcards,
  defaultVignettes,
} from '../../../utils/nbmeExamUtils';
import { ArrowLeft } from 'react-feather';
import CustomExamDatePicker from '../../../components/Global/CustomExamDatePicker';

interface ExamCompletionProps {
  handleDateChangeOrConfirmation: (
    examDate: Moment | null,
    ignored: boolean
  ) => void;
  closeSecondaryScreen: () => void;
  handleAddSchoolExam: () => void;
  exam: UserExam;
}

const ExamCompletion: React.FC<ExamCompletionProps> = ({
  handleDateChangeOrConfirmation,
  closeSecondaryScreen,
  handleAddSchoolExam,
  exam,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [availableExams, setAvailableExams] = useState<UserExam[] | []>([]);

  const [examCompleted, setExamCompleted] = useState<boolean | null>(
    exam?.hasCompletedRealExam ? true : null
  );
  const [showConfetti, setShowConfetti] = useState(false);

  const [examDate, setExamDate] = useState<Moment | null>(
    primaryExam?.examDate ? moment.utc(primaryExam.examDate) : null
  );

  useEffect(() => {
    getAvailableExams();
  }, []);

  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();
  const { updateExam } = useUserExamUpdate(refreshStudySessionAndContent, {
    showSuccessMessage: false,
  });

  const { width, height } = useWindowSize();

  const getAvailableExams = async () => {
    const response = await fetchExams();
    if (response) {
      const filteredExams = response.filter(
        (exam: UserExam) => exam.id !== primaryExam?.id
      );
      const nonComplettedFilteredExams = filteredExams.filter(
        (exam: UserExam) => !exam.hasCompletedRealExam
      );
      setAvailableExams(nonComplettedFilteredExams);
    }
  };

  const completePrimaryExamAndSetNextExam = async (nextExam?: UserExam) => {
    if (!currentUser || !primaryExam) return;

    await updateExam({
      userId: currentUser.id,
      examId: primaryExam.id,
      hasCompletedRealExam: true,
    });
    if (nextExam) {
      await updateExam({
        userId: currentUser.id,
        examId: nextExam.id,
        examName: nextExam.name,
        ...(nextExam.selectedFlashcardCount && nextExam.selectedVignetteCount
          ? {}
          : {
              selectedFlashcardCount:
                nextExam.selectedFlashcardCount ?? defaultFlashcards(nextExam),
              selectedVignetteCount:
                nextExam.selectedVignetteCount ?? defaultVignettes(nextExam),
            }),
      });
    }
    closeSecondaryScreen();
  };

  const handleGoHome = async () => {
    if (!currentUser || !primaryExam) return;
    hapticsImpactLight();

    await updateExam({
      userId: currentUser.id,
      examId: primaryExam.id,
      hasCompletedRealExam: true,
    });
    closeSecondaryScreen();
  };

  return (
    <div className="exam-completion">
      {examCompleted === null && (
        <div className="exam-completion_screen">
          <h1>
            Did you take your {primaryExam?.name}
            {primaryExam?.name?.toLowerCase().includes('exam') ? '?' : ' exam?'}
          </h1>
          <div className="button-container">
            <button
              className="button button--glass"
              onClick={() => setExamCompleted(false)}
            >
              Not Yet
            </button>
            <button
              className="button button--glass"
              onClick={() => {
                setShowConfetti(true);
                setExamCompleted(true);
                hapticsImpactLight();
              }}
            >
              Yes
            </button>
          </div>
        </div>
      )}
      {examCompleted === false && (
        <div className="exam-completion_screen">
          <h1>Modify your exam date</h1>
          <p className="m-b-1-50">
            Choose a new exam date to continue preparing.
          </p>
          <div className="block">
            <div className="exam-modify-selector">
              <div className="date-select-wrapper">
                <CustomExamDatePicker
                  value={examDate}
                  onChange={(newValue: Moment | null | undefined) => {
                    setExamDate(newValue ? moment.utc(newValue) : null);
                  }}
                />
              </div>
              <button
                className="button button--glass"
                onClick={() =>
                  handleDateChangeOrConfirmation(
                    examDate,
                    !!examDate?.isSame(moment.utc(primaryExam?.examDate), 'day')
                  )
                }
                disabled={examDate?.isSame(
                  moment.utc(primaryExam?.examDate),
                  'day'
                )}
              >
                Confirm Date
              </button>
            </div>
          </div>
          <div className="exam-completion_screen_footer">
            <button
              className="button button--link button--back"
              onClick={() => setExamCompleted(null)}
            >
              <ArrowLeft />
              Back
            </button>
          </div>
        </div>
      )}
      {examCompleted === true && (
        <div className="exam-completion_screen exam-completion_screen--congrats">
          <div className="exam-completion_screen_header">
            <div className="check-wrapper">
              <AnimatedCheckmark />
            </div>
            <h1>Congrats!</h1>
            <p style={{ textAlign: 'center' }}>
              What exam would you like to study next?
            </p>
          </div>
          <ExamAccordion
            availableExams={availableExams}
            onClick={(examId) => {
              const selectedExam = availableExams.find(
                (exam) => exam.id === examId
              );
              if (selectedExam) {
                completePrimaryExamAndSetNextExam(selectedExam);
              }
            }}
            createSchoolExamClick={handleAddSchoolExam}
          />
          <div className="or">or</div>
          <div
            className="exam-completion_screen_footer"
            style={{ paddingBottom: '1.5rem' }}
          >
            <button
              className="button button--link button--back"
              onClick={() => {
                handleGoHome();
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      )}
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          colors={['#FFFFFF', '#FFFFFF90', '#FFFFFF80']}
          recycle={false}
        />
      )}
    </div>
  );
};

export default ExamCompletion;
